package co.maytes.standupbot.dashboard.components.layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.UserMenu
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMenu
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.dom.Img
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px

@Composable
fun TopBar(
    onMenuClick: (() -> Unit)? = null,
    mobileOnly: Boolean = false,
) {
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val user = auth.currentUser

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(60.px)
            .classNames("sb-nav")
            .let { if (mobileOnly) it.classNames("sb-mobile-only") else it }
            .padding(leftRight = Spacing.lg)
            .styleModifier {
                property("position", "sticky")
                property("top", "0")
                property("z-index", "50")
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        ) {
            if (onMenuClick != null) {
                IconButton(
                    ariaLabel = "Open navigation menu",
                    modifier = Modifier
                        .classNames("sb-mobile-only")
                        .padding(Spacing.xxs)
                        .color(Palette.textSecondary),
                    onClick = { onMenuClick() },
                ) {
                    Box(modifier = Modifier.ariaHidden()) {
                        LucideMenu(size = 18)
                    }
                }
            }
            com.varabyte.kobweb.silk.components.navigation.Link(
                path = "/",
                modifier = Modifier.styleModifier {
                    property("text-decoration", "none")
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", "${Spacing.xs}")
                },
            ) {
                Img(
                    src = "/logo.svg",
                    attrs = Modifier
                        .styleModifier {
                            property("width", "28px")
                            property("height", "28px")
                            property("flex-shrink", "0")
                            property("display", "block")
                        }
                        .toAttrs { alt("StandupBot") },
                )
                SpanText(
                    text = "Standup Bot",
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary)
                        .styleModifier { property("letter-spacing", "-0.01em") },
                )
            }
            user?.workspace?.name?.let { ws ->
                Box(
                    modifier = Modifier
                        .width(1.px)
                        .height(18.px)
                        .backgroundColor(Palette.border),
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier
                        .styleModifier {
                            property("gap", "7px")
                            property("background-color", Palette.surfaceSubtle.cssRgb())
                            property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                            property("border-radius", "999px")
                            property("padding", "4px 10px 4px 9px")
                        },
                ) {
                    Box(
                        modifier = Modifier
                            .width(6.px)
                            .height(6.px)
                            .backgroundColor(Palette.accent)
                            .borderRadius(Radius.full),
                    )
                    SpanText(
                        text = ws,
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .fontWeight(500)
                            .color(Palette.textSecondary)
                            .styleModifier {
                                property("font-family", Typography.FONT_MONO)
                                property("letter-spacing", "-0.01em")
                            },
                    )
                }
            }
        }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
        ) {
            if (user != null) {
                UserMenu(
                    user = user,
                    isAdmin = auth.isAdmin,
                    onSettings = { ctx.router.navigateTo("/app/settings") },
                    onViewOnboarding = { ctx.router.navigateTo("/app/onboarding/welcome") },
                    onSignOut = {
                        scope.launch {
                            auth.signOut()
                            ui.showToast("Signed out", ToastKind.Success)
                            ctx.router.navigateTo("/")
                        }
                    },
                )
            }
        }
    }
}
